/**
 * BrandInfoTab — US-37.2
 *
 * Settings tab for editing brand information: identity, legal info, and branding.
 */

import { useState, useEffect } from 'react';
import { Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui';
import { Input } from '@/components/ui';
import { Select } from '@/components/ui';
import { Skeleton } from '@/components/ui';
import {
  useBrandSettings,
  useUpdateBrandSettings,
} from '../../hooks/useBrandSettings';
import type { UpdateBrandSettingsDto } from '@bstock/shared';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const COUNTRY_LABELS: Record<string, string> = {
  FR: 'France',
  MA: 'Maroc',
  CH: 'Suisse',
  BE: 'Belgique',
  LU: 'Luxembourg',
};

function getLegalIdLabel(country: string): string {
  if (country === 'MA') return 'ICE';
  return 'SIRET';
}

const CUTOFF_HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: String(i),
  label: `${i}h`,
}));

// ---------------------------------------------------------------------------
// Section wrapper
// ---------------------------------------------------------------------------

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-8">
      <h3 className="text-sm font-semibold text-neutral-700 uppercase tracking-wide mb-4">
        {title}
      </h3>
      <div className="bg-white rounded-xl border border-neutral-200 p-6 space-y-4">
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skeleton loading state
// ---------------------------------------------------------------------------

function BrandInfoSkeleton() {
  return (
    <div className="space-y-8">
      {[1, 2].map((i) => (
        <div key={i}>
          <Skeleton className="h-4 w-24 mb-4" />
          <div className="bg-white rounded-xl border border-neutral-200 p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function BrandInfoTab() {
  const { data, isLoading } = useBrandSettings();
  const updateMutation = useUpdateBrandSettings();

  const [form, setForm] = useState<UpdateBrandSettingsDto>({
    name: '',
    address: null,
    city: null,
    postalCode: null,
    phone: null,
    email: null,
    legalId: null,
    cutoffHour: 7,
  });
  const [hasChanges, setHasChanges] = useState(false);

  // Sync form when data loads
  useEffect(() => {
    if (data) {
      setForm({
        name: data.name,
        address: data.address,
        city: data.city,
        postalCode: data.postalCode,
        phone: data.phone,
        email: data.email,
        legalId: data.legalId,
        cutoffHour: data.cutoffHour,
      });
      setHasChanges(false);
    }
  }, [data]);

  const handleChange = (field: keyof UpdateBrandSettingsDto, value: string | null) => {
    setForm((prev) => ({ ...prev, [field]: value || null }));
    setHasChanges(true);
  };

  const handleSave = () => {
    updateMutation.mutate(form, {
      onSuccess: () => setHasChanges(false),
    });
  };

  if (isLoading) return <BrandInfoSkeleton />;

  const country = data?.country ?? 'FR';
  const legalIdLabel = getLegalIdLabel(country);

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-neutral-900">Informations de la marque</h2>
        <p className="text-sm text-neutral-500 mt-1">
          Configurez les informations de votre etablissement
        </p>
      </div>

      {/* Section Identite */}
      <Section title="Identite">
        {/* Nom — readonly */}
        <Input
          label="Nom"
          value={form.name ?? ''}
          readOnly
          disabled
          helperText="Le nom de la marque ne peut pas etre modifie ici"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Pays"
            value={COUNTRY_LABELS[country] ?? country}
            readOnly
            disabled
            helperText="Le pays est defini au niveau de l'entreprise"
          />
          <Input
            label="Ville"
            value={form.city ?? ''}
            placeholder="Paris"
            onChange={(e) => handleChange('city', e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Adresse"
            value={form.address ?? ''}
            placeholder="12 rue de la Paix"
            onChange={(e) => handleChange('address', e.target.value)}
          />
          <Input
            label="Code postal"
            value={form.postalCode ?? ''}
            placeholder="75001"
            onChange={(e) => handleChange('postalCode', e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Telephone"
            type="tel"
            value={form.phone ?? ''}
            placeholder="+33 1 23 45 67 89"
            onChange={(e) => handleChange('phone', e.target.value)}
          />
          <Input
            label="Email"
            type="email"
            value={form.email ?? ''}
            placeholder="contact@monrestaurant.fr"
            onChange={(e) => handleChange('email', e.target.value)}
          />
        </div>
      </Section>

      {/* Section Informations legales */}
      <Section title="Informations legales">
        <Input
          label={legalIdLabel}
          value={form.legalId ?? ''}
          placeholder={
            legalIdLabel === 'SIRET' ? '123 456 789 00012' : 'ICE 000123456789000'
          }
          onChange={(e) => handleChange('legalId', e.target.value)}
        />
      </Section>

      {/* Section Inventaires & Rapports */}
      <Section title="Inventaires & Rapports">
        <div className="max-w-xs">
          <Select
            label="Heure de coupure"
            value={String(form.cutoffHour ?? 7)}
            onChange={(value) => {
              setForm((prev) => ({ ...prev, cutoffHour: parseInt(value, 10) }));
              setHasChanges(true);
            }}
            options={CUTOFF_HOUR_OPTIONS}
          />
          <p className="text-xs text-neutral-500 mt-1">
            Heure utilisee par defaut pour les inventaires et les filtres de rapports
          </p>
        </div>
      </Section>

      {/* Footer actions */}
      <div className="flex items-center justify-end gap-3 pt-6 border-t border-neutral-200">
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!hasChanges || updateMutation.isPending}
        >
          {updateMutation.isPending ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Enregistrer
        </Button>
      </div>
    </div>
  );
}
